<template>
  <modal v-if="$modal" class="modal" :class="{
    'modal_w-footer': hasFooter,
    'modal_scrollable': scrollable
  }" :name="name" :pivotY="0.1" :width="width" :height="height" adaptive overlayTransition="" @opened="onModalOpened"
    @closed="onModalClosed" @before-open="$emit('before-open', $event)">
    <div class="modal__body" ref="modalBody">
      <button v-if="closeButton" class="modal__close-button" @click="$modal.hide(name)">
        <img src="@/assets/icon-x.svg" />
      </button>

      <slot />
    </div>
    <footer v-if="hasFooter" class="modal__footer">
      <slot name="footer" />
    </footer>
  </modal>
</template>

<script>
import * as bodyScroll from 'tua-body-scroll-lock';

export default {
  props: {
    name: String,
    width: Number,
    height: {},
    closeButton: Boolean,
    scrollable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isVisible: false,
      touchStartY: 0,
      touchStartX: 0,
    };
  },
  computed: {
    hasFooter() {
      return Boolean(this.$slots.footer);
    },
  },
  methods: {
    onModalOpened() {
      this.isVisible = true;
      this.lockScroll();
      this.$emit('opened');

      // Add touch event listeners if scrollable
      if (this.scrollable && this.$refs.modalBody) {
        this.$refs.modalBody.addEventListener('touchstart', this.handleTouchStart, { passive: false });
        this.$refs.modalBody.addEventListener('touchmove', this.handleTouchMove, { passive: false });
      }

      // Add ESC key handler
      document.addEventListener('keydown', this.handleKeyDown);

      // Add click handler for the overlay to close the modal when clicking outside
      this.setupOutsideClickHandler();
    },

    setupOutsideClickHandler() {
      // Wait a bit for the modal to be fully rendered
      setTimeout(() => {
        const overlays = document.querySelectorAll('.vm--overlay');
        if (overlays && overlays.length) {
          const modalOverlay = Array.from(overlays).find(
            overlay => overlay.style.display !== 'none',
          );

          if (modalOverlay) {
            const handleOutsideClick = (e) => {
              // Only handle clicks directly on the overlay (not its children)
              if (e.target === modalOverlay) {
                this.$modal.hide(this.name);
                // Emit before-close event so parent components can handle it
                this.$emit('before-close', { type: 'outside-click' });
                modalOverlay.removeEventListener('click', handleOutsideClick);
              }
            };

            modalOverlay.addEventListener('click', handleOutsideClick);
          }
        }
      }, 100);
    },

    onModalClosed() {
      this.isVisible = false;
      this.unlockScroll();
      this.$emit('closed');

      // Remove touch event listeners
      if (this.scrollable && this.$refs.modalBody) {
        this.$refs.modalBody.removeEventListener('touchstart', this.handleTouchStart);
        this.$refs.modalBody.removeEventListener('touchmove', this.handleTouchMove);
      }

      // Remove ESC key handler
      document.removeEventListener('keydown', this.handleKeyDown);
    },
    lockScroll() {
      if (this.$refs.modalBody) {
        bodyScroll.lock(this.$refs.modalBody);
      }
    },
    unlockScroll() {
      bodyScroll.unlock(this.$refs.modalBody);
    },
    handleTouchStart(e) {
      // Store the initial touch position
      this.touchStartY = e.touches[0].clientY;
      this.touchStartX = e.touches[0].clientX;
    },
    handleTouchMove(e) {
      if (!this.$refs.modalBody || e.touches.length !== 1) return;

      const touchY = e.touches[0].clientY;
      const touchX = e.touches[0].clientX;
      const el = this.$refs.modalBody;

      // Calculate touch difference
      const touchDiffY = touchY - this.touchStartY;
      const touchDiffX = touchX - this.touchStartX;

      // Determine if scroll is vertical
      const isVertical = Math.abs(touchDiffY) > Math.abs(touchDiffX);

      if (isVertical) {
        const isScrollingUp = touchDiffY > 0;
        const isScrollingDown = touchDiffY < 0;
        const isAtTop = el.scrollTop <= 0;
        const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight;

        // Prevent default only if:
        // - Scrolling up and already at the top OR
        // - Scrolling down and already at the bottom
        if ((isScrollingUp && isAtTop) || (isScrollingDown && isAtBottom)) {
          e.preventDefault();
        }
      }
    },
    handleKeyDown(e) {
      // Close modal on ESC key
      if (e.key === 'Escape' || e.keyCode === 27) {
        this.$modal.hide(this.name);
        // Emit before-close event with cancel flag for ESC key
        this.$emit('before-close', { cancel: true });
      }
    },
  },
  beforeDestroy() {
    this.unlockScroll();

    // Clean up any event listeners
    if (this.scrollable && this.$refs.modalBody) {
      this.$refs.modalBody.removeEventListener('touchstart', this.handleTouchStart);
      this.$refs.modalBody.removeEventListener('touchmove', this.handleTouchMove);
    }
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.modal {
  &::v-deep {
    .vm--modal {
      max-height: 100vh;
      max-width: 100%;
      overflow: auto;
      background-color: var(--bg);
      border: 2px solid var(--lighter-primary);
    }

    .vm--overlay {
      background: rgba(0, 0, 0, var(--modal-overlay-opacity));
    }
  }

  &_w-footer {
    .modal__body {
      padding-bottom: 10px;
    }
  }

  &_scrollable {
    .modal__body {
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      /* Enables momentum scrolling on iOS */
    }
  }

  &__close-button {
    filter: var(--img-filter-inverted);
    position: absolute;
    top: 5px;
    right: 5px;

    img {
      width: 20px;
    }
  }

  &__body {
    position: relative;
    padding: 15px;
    overflow: auto;
    height: 100%;
  }

  &__footer {
    display: flex;
    padding: 10px 15px 15px;
    border-top: 1px solid var(--tertiary-accent);
  }
}
</style>
